import { Nullable } from '@dxb-web-ui-framework/common/types';
import { isEmpty } from '@dxb-web-ui-framework/common/utils/Helper';
import { Box, CardHeading, Pagination, makeStyles } from '@dxb-web-ui-framework/design-system';
import React, { useCallback, ChangeEvent, useState, VFC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { ContentWrapper } from '../../../../content-composer/src/components/Generic/ContentWrapper';
import { ErrorAlert } from '../../../../content-composer/src/components/Generic/ErrorAlert/ErrorAlert';
import { TableGrid } from '../../../../content-composer/src/components/Generic/TableGrid';
import { getRoutePath } from '../../../../content-composer/src/components/Routes/routePaths';
import usePagination from '../../../../content-composer/src/hooks/usePagination';
import { useSelectedCustomerId } from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { PayrollDetailsDrawer, PayrollDetailsContent } from '../PayrollDetailsDrawer';
import { usePayrollGridConfig } from '../PayrollTableGridConfigurations';
import { Payroll } from '../../domain/Payroll/entities/types';
import {
  useOnResetSelectedPayroll,
  useOnSelectPayroll,
  usePayrollDetails,
  usePayrollErrorState,
  usePayrollLoadingState,
} from '../../contexts/PayrollFile/hooks';

export interface PayrollTableProps {
  payrolls: Nullable<Payroll[]>;
  isLoading: boolean;
  payrollListError?: any;
  totalPages: number;
  currentPage: number;
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
}

const useStyles = makeStyles(theme => ({
  typographyValue: {
    fontSize: theme.typography.body2.fontSize,
  },
  tableActionButton: {
    padding: theme.spacing(0),
  },
}));

const PayrollTable: VFC<PayrollTableProps> = ({ payrolls, isLoading, payrollListError }) => {
  const [isPayrollDetailsDrawerOpen, setIsPayrollDetailsDrawerOpen] = useState(false);
  const customerId = useSelectedCustomerId();
  const { count, currentPage, setCurrentPage, setData, jump, currentData } = usePagination(payrolls || [], 10);
  const { isExtraSmallScreen: isMobile } = useScreenDetector();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const classes = useStyles();

  // payroll hooks
  const payrollDetails = usePayrollDetails();
  const isLoadingPayrollDetails = usePayrollLoadingState('isLoadingPayrollDetails');
  const hasErrorPayrollDetails = usePayrollErrorState('payrollDetailsError');
  const resetSelectedPayroll = useOnResetSelectedPayroll();
  const onSelectPayroll = useOnSelectPayroll();

  useEffect(() => {
    setData(payrolls || []);
    setCurrentPage(1);
  }, [payrolls, setCurrentPage, setData]);

  const onTogglePayrollDetailsDrawer = useCallback(
    (id: number) => {
      if (!isPayrollDetailsDrawerOpen) {
        onSelectPayroll(id);
      } else {
        resetSelectedPayroll();
      }
      setIsPayrollDetailsDrawerOpen(prev => !prev);
    },
    [isPayrollDetailsDrawerOpen, onSelectPayroll, resetSelectedPayroll],
  );

  const onClickPayrollMobileListItem = useCallback(
    (rowData: Payroll) => {
      if (rowData.id) {
        onSelectPayroll(rowData.id);
      }
      navigate(getRoutePath({ routeKey: 'payroll-details', customerId, fileId: rowData.id }));
    },
    [navigate, customerId, onSelectPayroll],
  );

  const config = usePayrollGridConfig(onTogglePayrollDetailsDrawer, onClickPayrollMobileListItem, classes);

  const shouldShowTable = isEmpty(payrollListError);

  const onChangePage = useCallback(
    (event: ChangeEvent<unknown>, page: number) => {
      setCurrentPage(page);
      jump(page);
    },
    [jump, setCurrentPage],
  );

  return (
    <>
      <Box pl={isMobile ? 3 : 0}>
        <CardHeading heading={t('payrollPage.PayrollTable.CardHeading.Heading')} hideOnMobile={false} />
      </Box>

      <ContentWrapper paperProps={{ style: { padding: 0 } }} boxProps={{ padding: 0 }}>
        {!isEmpty(payrollListError) && <ErrorAlert message={payrollListError.message} />}

        {shouldShowTable && (
          <TableGrid
            gridOptions={config.gridOptions}
            columnDefs={config.columnDefs}
            rowData={currentData()}
            hasSkeleton={isLoading}
            mobileListConfig={config.mobileListConfig}
          >
            {count > 1 && (
              <Pagination
                siblingCount={isMobile ? 0 : undefined}
                color='primary'
                count={count}
                page={currentPage}
                onChange={onChangePage}
              />
            )}
          </TableGrid>
        )}
      </ContentWrapper>
      {!isMobile && (
        <PayrollDetailsDrawer isOpen={isPayrollDetailsDrawerOpen} onToggleDrawer={onTogglePayrollDetailsDrawer}>
          <PayrollDetailsContent
            payrollDetails={payrollDetails}
            isLoading={isLoadingPayrollDetails}
            error={hasErrorPayrollDetails}
          />
        </PayrollDetailsDrawer>
      )}
    </>
  );
};

export { PayrollTable };
